import React, { useState, useEffect } from 'react'
import { liabilityService } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { formatCurrency } from '../utils/currency'
import { formatDate, formatDateForInput } from '../utils/dateFormat'
import { getApiUrl } from '../utils/apiConfig'
import TablePagination from '../components/TablePagination'
import ModalFeedback from '../components/ModalFeedback'
import useRolePermissions from '../hooks/useRolePermissions'

export default function Liabilities() {
  const { user } = useAuth()
  const { isDark } = useTheme()
  const { canMutate, isViewer } = useRolePermissions()
  const [liabilities, setLiabilities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [showModal, setShowModal] = useState(false)
  const [modalData, setModalData] = useState(null)
  const [modalType, setModalType] = useState('create')

  useEffect(() => {
    fetchLiabilities()
  }, [])

  const fetchLiabilities = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await liabilityService.getAll()
      setLiabilities(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch liabilities:', error)
      setError(error.response?.data?.message || error.message || 'Failed to fetch liabilities')
    } finally {
      setLoading(false)
    }
  }

  const filteredLiabilities = liabilities.filter(liability => {
    const matchesSearch = searchTerm === '' ||
      liability.liability_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      liability.liability_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (liability.lender && liability.lender.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesStatus = statusFilter === '' || liability.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const paginatedLiabilities = filteredLiabilities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const handleCreate = () => {
    setModalType('create')
    setModalData({
      liability_code: '',
      liability_name: '',
      category_id: '',
      lender: '',
      principal_amount: '',
      outstanding_balance: '',
      interest_rate: '',
      start_date: '',
      maturity_date: '',
      payment_frequency: 'Monthly',
      payment_amount: '',
      next_payment_date: '',
      purpose: '',
      terms_conditions: '',
      // Financial integration options
      record_cash_receipt: true,
      loan_account_id: ''
    })
    setShowModal(true)
  }

  const handleEdit = (liability) => {
    setModalType('edit')
    setModalData({ ...liability })
    setShowModal(true)
  }

  const handleDelete = async (liabilityId) => {
    if (!confirm('Are you sure you want to delete this liability?')) return

    try {
      await liabilityService.delete(liabilityId)
      await fetchLiabilities()
      showSuccess('Liability deleted successfully')
    } catch (error) {
      console.error('Failed to delete liability:', error)
      showError(error.response?.data?.message || 'Failed to delete liability')
    }
  }

  const handleSave = async (formData) => {
    try {
      if (modalType === 'create') {
        // First create the liability
        const liabilityResponse = await liabilityService.create(formData)

        // If cash receipt recording is requested, create journal entry
        if (formData.record_cash_receipt && formData.principal_amount) {
          await createLoanJournalEntry({
            liabilityId: liabilityResponse.data.liability_id,
            liabilityName: formData.liability_name,
            principalAmount: parseFloat(formData.principal_amount),
            loanAccountId: formData.loan_account_id,
            startDate: formData.start_date
          })
          showSuccess('Loan created with cash receipt recorded successfully')
        } else {
          showSuccess('Liability created successfully')
        }
      } else {
        await liabilityService.update(formData.liability_id, formData)
        showSuccess('Liability updated successfully')
      }
      setShowModal(false)
      await fetchLiabilities()
    } catch (error) {
      console.error('Failed to save liability:', error)
      showError(error.response?.data?.message || 'Failed to save liability')
    }
  }

  const createLoanJournalEntry = async ({ liabilityId, liabilityName, principalAmount, loanAccountId, startDate }) => {
    try {
      const token = localStorage.getItem('token')
      // Map loan account type to actual account ID
      const accountMapping = {
        'short_term': 9,   // Short-term Loans
        'long_term': 12     // Long-term Loans
      }

      const entryData = {
        entry_date: startDate,
        reference: `LOAN-${liabilityId}`,
        description: `Loan Disbursement: ${liabilityName}`,
        lines: [
          {
            account_id: 1, // Cash and Bank
            debit_amount: principalAmount,
            credit_amount: 0,
            description: `Loan proceeds received: ${liabilityName}`
          },
          {
            account_id: accountMapping[loanAccountId] || 9, // Default to short-term if not specified
            debit_amount: 0,
            credit_amount: principalAmount,
            description: `Loan payable: ${liabilityName}`
          }
        ]
      }

      await fetch(getApiUrl('/journal-entries'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(entryData)
      })
    } catch (error) {
      console.error('Failed to create journal entry:', error)
      throw error
    }
  }

  const handlePayment = async (liabilityId, liabilityName) => {
    const principal = prompt('Enter principal payment amount:')
    if (!principal || isNaN(principal)) return

    const interest = prompt('Enter interest payment amount:')
    if (interest === null) return

    try {
      await liabilityService.recordPayment(liabilityId, {
        payment_date: new Date().toISOString().split('T')[0],
        principal_amount: parseFloat(principal) || 0,
        interest_amount: parseFloat(interest) || 0,
        payment_method: 'Bank Transfer'
      })
      showSuccess('Payment recorded successfully')
      await fetchLiabilities()
    } catch (error) {
      console.error('Failed to record payment:', error)
      showError(error.response?.data?.message || 'Failed to record payment')
    }
  }

  const showSuccess = (message) => {
    setModalData({ type: 'success', message })
    setModalType('feedback')
    setShowModal(true)
  }

  const showError = (message) => {
    setModalData({ type: 'error', message })
    setModalType('feedback')
    setShowModal(true)
  }

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800',
      'Restructured': 'bg-yellow-100 text-yellow-800',
      'Paid': 'bg-blue-100 text-blue-800',
      'Defaulted': 'bg-red-100 text-red-800',
      'Settled': 'bg-purple-100 text-purple-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getMaturityStatus = (maturityDate) => {
    if (!maturityDate) return null
    const today = new Date()
    const maturity = new Date(maturityDate)
    const daysDiff = Math.ceil((maturity - today) / (1000 * 60 * 60 * 24))

    if (daysDiff < 0) return { text: 'Overdue', color: 'bg-red-100 text-red-800' }
    if (daysDiff <= 30) return { text: 'Urgent', color: 'bg-orange-100 text-orange-800' }
    if (daysDiff <= 90) return { text: 'Upcoming', color: 'bg-yellow-100 text-yellow-800' }
    return { text: 'Future', color: 'bg-green-100 text-green-800' }
  }

  if (loading) {
    return (
      <div className={`rounded-lg p-8 text-center ${isDark ? 'bg-slate-900 text-white' : 'bg-white'}`}>
        <div className="text-4xl mb-4">⏳</div>
        <p className={isDark ? 'text-slate-300' : 'text-gray-600'}>Loading liabilities...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`rounded-lg p-8 text-center ${isDark ? 'bg-slate-900 text-white' : 'bg-white'}`}>
        <div className="text-4xl mb-4">⚠️</div>
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchLiabilities}
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Liabilities Management</h1>
        {canMutate && (
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
          >
            + Add Liability
          </button>
        )}
      </div>

      {/* Filters */}
      <div className={`rounded-lg p-4 shadow ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, code, or lender..."
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'border-slate-600 bg-slate-800 text-white' : 'border-gray-300 text-gray-700'
              }`}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'border-slate-600 bg-slate-800 text-white' : 'border-gray-300 text-gray-700'
              }`}
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Restructured">Restructured</option>
              <option value="Paid">Paid</option>
              <option value="Defaulted">Defaulted</option>
              <option value="Settled">Settled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liabilities Table */}
      <div className={`rounded-lg shadow overflow-hidden ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="overflow-x-auto">
          <table className={`w-full ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            <thead className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Liability Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Lender</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Principal</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Outstanding</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Interest Rate</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Maturity</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
              {paginatedLiabilities.length > 0 ? (
                paginatedLiabilities.map((liability) => {
                  const maturityStatus = getMaturityStatus(liability.maturity_date)
                  const repaymentPercentage = liability.principal_amount > 0
                    ? ((liability.principal_amount - liability.outstanding_balance) / liability.principal_amount * 100).toFixed(1)
                    : 0

                  return (
                    <tr key={liability.liability_id} className={isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3 text-sm font-medium">{liability.liability_code}</td>
                      <td className="px-4 py-3 text-sm">{liability.liability_name}</td>
                      <td className="px-4 py-3 text-sm">{liability.lender || '-'}</td>
                      <td className="px-4 py-3 text-sm">{formatCurrency(liability.principal_amount)}</td>
                      <td className="px-4 py-3 text-sm font-medium">{formatCurrency(liability.outstanding_balance)}</td>
                      <td className="px-4 py-3 text-sm">{liability.interest_rate ? `${liability.interest_rate}%` : '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        {liability.maturity_date ? (
                          <div>
                            <div>{formatDate(liability.maturity_date)}</div>
                            {maturityStatus && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium mt-1 inline-block ${maturityStatus.color}`}>
                                {maturityStatus.text}
                              </span>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(liability.status)}`}>
                          {liability.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex justify-end gap-2">
                          {canMutate && (
                            <>
                              <button
                                onClick={() => handleEdit(liability)}
                                className="text-blue-600 hover:text-blue-800 font-medium text-xs"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handlePayment(liability.liability_id, liability.liability_name)}
                                className="text-green-600 hover:text-green-800 font-medium text-xs"
                                disabled={liability.status !== 'Active'}
                              >
                                Pay
                              </button>
                              <button
                                onClick={() => handleDelete(liability.liability_id)}
                                className="text-red-600 hover:text-red-800 font-medium text-xs"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-sm">
                    No liabilities found. {canMutate && 'Click "Add Liability" to create one.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          totalItems={filteredLiabilities.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>

      {/* Liability Modal */}
      {showModal && modalType !== 'feedback' && (
        <LiabilityModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          initialData={modalData}
          type={modalType}
          isDark={isDark}
        />
      )}

      {/* Feedback Modal */}
      {showModal && modalType === 'feedback' && (
        <ModalFeedback
          show={showModal}
          onClose={() => setShowModal(false)}
          type={modalData?.type}
          message={modalData?.message}
        />
      )}
    </div>
  )
}

// Liability Modal Component
function LiabilityModal({ show, onClose, onSave, initialData, type, isDark }) {
  const [formData, setFormData] = useState(initialData || {})
  const [errors, setErrors] = useState({})

  useEffect(() => {
    setFormData(initialData || {})
  }, [initialData])

  const validate = () => {
    const newErrors = {}
    if (!formData.liability_name) newErrors.liability_name = 'Liability name is required'
    if (!formData.principal_amount) newErrors.principal_amount = 'Principal amount is required'
    if (!formData.start_date) newErrors.start_date = 'Start date is required'
    if (!formData.maturity_date) newErrors.maturity_date = 'Maturity date is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validate()) {
      onSave(formData)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-slate-800 text-white' : 'bg-white text-gray-800'}`}>
        <div className={`p-6 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <h2 className="text-xl font-bold">
            {type === 'create' ? 'Create New Liability' : 'Edit Liability'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Liability Code</label>
              <input
                type="text"
                value={formData.liability_code || ''}
                onChange={(e) => setFormData({ ...formData, liability_code: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
                placeholder="Auto-generated if empty"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Liability Name *</label>
              <input
                type="text"
                value={formData.liability_name || ''}
                onChange={(e) => setFormData({ ...formData, liability_name: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
              {errors.liability_name && <p className="text-red-500 text-xs mt-1">{errors.liability_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Lender</label>
              <input
                type="text"
                value={formData.lender || ''}
                onChange={(e) => setFormData({ ...formData, lender: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Principal Amount *</label>
              <input
                type="number"
                step="0.01"
                value={formData.principal_amount || ''}
                onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
              {errors.principal_amount && <p className="text-red-500 text-xs mt-1">{errors.principal_amount}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Outstanding Balance</label>
              <input
                type="number"
                step="0.01"
                value={formData.outstanding_balance || ''}
                onChange={(e) => setFormData({ ...formData, outstanding_balance: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Interest Rate (%)</label>
              <input
                type="number"
                step="0.01"
                value={formData.interest_rate || ''}
                onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Start Date *</label>
              <input
                type="date"
                value={formData.start_date || ''}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
              {errors.start_date && <p className="text-red-500 text-xs mt-1">{errors.start_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Maturity Date *</label>
              <input
                type="date"
                value={formData.maturity_date || ''}
                onChange={(e) => setFormData({ ...formData, maturity_date: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
              {errors.maturity_date && <p className="text-red-500 text-xs mt-1">{errors.maturity_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payment Frequency</label>
              <select
                value={formData.payment_frequency || 'Monthly'}
                onChange={(e) => setFormData({ ...formData, payment_frequency: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              >
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-annually">Semi-annually</option>
                <option value="Annually">Annually</option>
                <option value="One-time">One-time</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payment Amount</label>
              <input
                type="number"
                step="0.01"
                value={formData.payment_amount || ''}
                onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Next Payment Date</label>
              <input
                type="date"
                value={formData.next_payment_date || ''}
                onChange={(e) => setFormData({ ...formData, next_payment_date: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={formData.status || 'Active'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
                }`}
              >
                <option value="Active">Active</option>
                <option value="Restructured">Restructured</option>
                <option value="Paid">Paid</option>
                <option value="Defaulted">Defaulted</option>
                <option value="Settled">Settled</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Purpose</label>
            <textarea
              value={formData.purpose || ''}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              rows="2"
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Terms & Conditions</label>
            <textarea
              value={formData.terms_conditions || ''}
              onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
              rows="3"
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'border-slate-600 bg-slate-700' : 'border-gray-300'
              }`}
            />
          </div>

          {/* Financial Integration Section */}
          {type === 'create' && (
            <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-green-50'}`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                <span>💰</span>
                <span>Cash Receipt Recording</span>
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.record_cash_receipt || false}
                      onChange={(e) => setFormData({ ...formData, record_cash_receipt: e.target.checked })}
                      className="rounded"
                    />
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
                      Record cash receipt for this loan?
                    </span>
                  </label>
                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                    Automatically record the loan proceeds as a cash receipt in your financial statements
                  </p>
                </div>

                {formData.record_cash_receipt && (
                  <>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                        Loan Account Type *
                      </label>
                      <select
                        value={formData.loan_account_id || ''}
                        onChange={(e) => setFormData({ ...formData, loan_account_id: e.target.value })}
                        className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDark ? 'border-slate-600 bg-slate-700 text-white' : 'border-gray-300 text-gray-700'
                        }`}
                        required
                      >
                        <option value="">Select Loan Account Type</option>
                        <option value="short_term">Short-term Loans (Account #1202)</option>
                        <option value="long_term">Long-term Loans (Account #1204)</option>
                      </select>
                    </div>

                    {/* Journal Entry Preview */}
                    <div className={`p-3 rounded ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                      <p className={`text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        📝 Automatic Journal Entry Preview:
                      </p>
                      <div className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                        <span className="font-medium text-green-600">Dr</span> Cash and Bank {formatCurrency(parseFloat(formData.principal_amount) || 0)}<br/>
                        <span className="font-medium text-red-600">Cr</span> {formData.loan_account_id === 'short_term' ? 'Short-term' : 'Long-term'} Loans {formatCurrency(parseFloat(formData.principal_amount) || 0)}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </form>
        <div className={`p-6 border-t flex justify-end gap-3 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 rounded-lg font-medium transition ${isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
          >
            {type === 'create' ? 'Create Liability' : 'Update Liability'}
          </button>
        </div>
      </div>
    </div>
  )
}